import type { Fund, FundOption } from './fund';
import type { FundDetail } from './fund-detail';
import type { InvestmentGoal } from './investment-goal';

/** 펀드 고르기 · 선택 상자 예시(Figma FundPicker · FundExcelDownload 와 같은 7건) */
export const fundOptionsFixture: readonly FundOption[] = [
  { idx: 1, name: '임팩트 1호 펀드' },
  { idx: 2, name: '소셜벤처 2호 펀드' },
  { idx: 3, name: '기후테크 3호 펀드' },
  { idx: 4, name: '스케일업 4호 펀드' },
  { idx: 5, name: '지역혁신 5호 펀드' },
  { idx: 6, name: '헬스케어 6호 펀드' },
  { idx: 7, name: '청년창업 7호 펀드' },
];

/** 견본의 고른 펀드(첫째 · 둘째 · 넷째) */
export const fundPickerValueFixture: readonly number[] = [1, 2, 4];

const fundBase: Fund = {
  idx: 1,
  name: '임팩트 1호 펀드',
  businessNumber: '123-45-67890',
  fundDuration: ['2021-03-15', '2029-03-14'],
  investDuration: ['2021-03-15', '2025-03-14'],
  fundingRound: ['Seed', 'Series A'],
  commentInvestmentTarget: '기후테크 · 헬스케어 초기 기업',
  volumeCashCapitalContribution: 13_300_000_000,
  volumeCashCapitalPayment: 13_300_000_000,
  volumeCashInvested: 10_180_000_000,
  volumeCashUninvested: 3_120_000_000,
  volumeCashInvestable: 3_120_000_000,
  volumeCashEquivalent: 850_000_000,
  volumeCashTotalExit: 4_250_000_000,
  volumeCashTotalValuation: 18_650_000_000,
  NumberOfExitComplete: 3,
  NumberOfExitPartial: 2,
  irr1: 0.184,
  irr2: 0.142,
  multiple: 1.8,
  multipleExit: 1.6,
  dpi: 0.58,
  rvpi: 1.24,
  tvpi: 1.83,
  burnrate: 0.625,
};

/**
 * 펀드 목록 예시(Figma FundRow · FundListSection 과 같은 3건).
 * 셋째 펀드는 irr1 · tvpi 가 기준 미만이라 "-" 로 찍힌다.
 * Figma 목록의 첫 줄 회수 건수는 3 이라 회수 2 + 부분회수 1 로 둔다(상세 예시는 3 · 2).
 */
export const fundListFixture: readonly Fund[] = [
  { ...fundBase, NumberOfExitComplete: 2, NumberOfExitPartial: 1 },
  {
    ...fundBase,
    idx: 2,
    name: '소셜벤처 2호 펀드',
    businessNumber: '234-56-78901',
    fundDuration: ['2022-06-30', '2030-06-29'],
    investDuration: ['2022-06-30', '2026-06-29'],
    fundingRound: ['Pre-A', 'Series B'],
    commentInvestmentTarget: '소셜벤처 · 사회적기업',
    volumeCashCapitalContribution: 8_500_000_000,
    volumeCashCapitalPayment: 8_500_000_000,
    volumeCashInvested: 4_100_000_000,
    volumeCashUninvested: 4_400_000_000,
    volumeCashInvestable: 4_400_000_000,
    NumberOfExitComplete: 1,
    NumberOfExitPartial: 0,
    irr1: 0.127,
    irr2: 0.098,
    multiple: 1.4,
    tvpi: 1.42,
    burnrate: 0.482,
  },
  {
    ...fundBase,
    idx: 3,
    name: '기후테크 3호 펀드',
    businessNumber: '345-67-89012',
    fundDuration: ['2023-11-01', '2031-10-31'],
    investDuration: ['2023-11-01', '2027-10-31'],
    fundingRound: ['Seed', 'Series A'],
    commentInvestmentTarget: '기후테크 · 에너지 전환',
    volumeCashCapitalContribution: 20_000_000_000,
    volumeCashCapitalPayment: 10_000_000_000,
    volumeCashInvested: 4_200_000_000,
    volumeCashUninvested: 15_800_000_000,
    volumeCashInvestable: 15_800_000_000,
    volumeCashTotalExit: 0,
    NumberOfExitComplete: 0,
    NumberOfExitPartial: 0,
    irr1: 0.004,
    irr2: 0.002,
    multiple: 0.95,
    tvpi: 0.92,
    burnrate: 0.21,
  },
];

/** 투자 목적 예시(Figma FundTargetItem · FundTargetSection 과 같은 4건). 단독 견본은 첫째(75%) */
export const investmentGoalsFixture: readonly InvestmentGoal[] = [
  {
    idx: 1,
    name: '주목적',
    volumeTarget: 20_000_000_000,
    volumeInvested: 15_000_000_000,
    isMajor: true,
  },
  {
    idx: 2,
    name: '소셜벤처',
    volumeTarget: 5_000_000_000,
    volumeInvested: 2_500_000_000,
    isMajor: false,
  },
  {
    idx: 3,
    name: '지역 기업',
    volumeTarget: 4_000_000_000,
    volumeInvested: 1_000_000_000,
    isMajor: false,
  },
  {
    idx: 4,
    name: '기후테크',
    volumeTarget: 3_000_000_000,
    volumeInvested: 3_000_000_000,
    isMajor: false,
  },
];

/** 펀드 상세 예시(임팩트 1호 펀드, Figma FundSummary · FundExitSummary · FundForm 과 같은 값) */
export const fundDetailFixture: FundDetail = {
  ...fundBase,
  commentInvestmentTarget:
    '기후테크 · 헬스케어 분야의 초기 기업에 투자한다.\n사회적 가치를 함께 만드는 기업을 우선한다.',
  investGoal: [...investmentGoalsFixture],
  // 회수 및 평가 총액 그래프 예시(2023.1Q ~ 2024.4Q, 분기 시작 직전 시점)
  quarterlyValuation: [4.2, 4.6, 5.1, 5.9, 6.3, 7.0, 7.6, 8.4].map((billion, i) => ({
    year: 2023 + Math.floor(i / 4),
    quarter: (i % 4) + 1,
    amount: Math.round(billion * 1_000_000_000),
  })),
  contribution: [
    { idx: 1, lp: { idx: 1, name: '예시 연기금 A' }, volume: 8_000_000_000 },
    { idx: 2, lp: { idx: 2, name: '예시 연기금 C' }, volume: 5_300_000_000 },
  ],
  payment: [
    { idx: 1, volume: 6_650_000_000, paidAt: '2021-03-15' },
    { idx: 2, volume: 6_650_000_000, paidAt: '2022-03-15' },
  ],
  distribution: [{ idx: 1, volume: 1_200_000_000, distributedAt: '2024-06-30' }],
};
